import { useCallback, useEffect, useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Divider from "@mui/material/Divider";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Download, ExternalLink, Send } from "lucide-react";
import {
  buildAgendaEmailHtml,
  type AgendaEmailData,
} from "@event-planner/shared";
import type {
  Audience,
  Session,
  Event,
  EventParticipant,
  Person,
} from "@event-planner/shared";
import { exportFilename } from "../../utils/exportFilename";
import { downloadCsv } from "../../utils/csv";
import { useEventVenue } from "../../hooks/useEventVenue";
import {
  buildAgendaCsvRows,
  type AudienceSpec,
  type CsvShape,
} from "../../utils/agendaCsv";
import { type AudienceLabelUser } from "@event-planner/shared";
import { ScaledEmailFrame } from "../../components/phone/ScaledEmailFrame";

// ── Types ────────────────────────────────────────────────────────────────

type ExportAction = "pdf" | "csv" | "email" | "sharepoint";

/**
 * Filter shape mirrors SessionListPage.FilterState. Flat (not a discriminated
 * union) so the page can update value slots via spread without narrowing
 * issues — the dialog reads only the slot relevant to the current mode.
 *
 * Group-centric (ADR-094): `role` slot is gone, replaced by `groupId`.
 */
export interface ExportFilter {
  mode: "none" | "type" | "group" | "user";
  typeId: string | null;
  groupId: string | null;
  user: Person | null;
}

interface ExportDialogProps {
  open: boolean;
  onClose: () => void;
  event: Event;
  /** Already-filtered sessions — same list the page is currently showing. */
  sessions: Session[];
  participants: EventParticipant[];
  persons: Person[];
  /**
   * All audiences for this event keyed by id (ADR-120). The CSV path
   * walks this map via formatAudienceLabel for each session's
   * audienceId, including descent into anonymous compositions.
   */
  audienceById: Map<string, Audience>;
  /** Lookup for personal-audience label resolution. */
  userById: Map<string, AudienceLabelUser>;
  /** The page's filter state. Drives PDF / CSV / email uniformly. */
  filter: ExportFilter;
  /** Human-readable summary of the filter, shown as a chip in the dialog. */
  filterSummary: string;
  /** Fires the PDF export (html2canvas + jsPDF on the already-filtered DOM). */
  onExportPdf: () => Promise<void>;
  /** Fires the sendAgendaEmail callable. */
  onSendEmail: (target: string) => Promise<void>;
  /**
   * Optional SharePoint deep-link for this event's sessions list. When set,
   * the dialog surfaces "Open in SharePoint" as a fourth action that opens
   * the URL in a new tab. Null/undefined hides the option entirely (e.g.
   * when SharePoint isn't configured for the deployment).
   */
  sharePointUrl?: string | null;
}

// ── Helpers ──────────────────────────────────────────────────────────────

/**
 * Derive the CSV audience spec from the page filter. Audience is orthogonal
 * to CSV shape — it determines WHICH members are eligible to appear in the
 * output, not the output structure:
 *   - "user"  → a single user's personal agenda (shape=personal)
 *   - "role"  → members of a specific role
 *   - "all"   → every event member (for none / type filters)
 */
function filterToAudienceSpec(filter: ExportFilter): AudienceSpec {
  if (filter.mode === "user" && filter.user) {
    return { kind: "user", userId: filter.user.id };
  }
  if (filter.mode === "group" && filter.groupId) {
    return { kind: "group", groupId: filter.groupId };
  }
  return { kind: "all" };
}

/**
 * Which CSV shape applies to a given filter mode. The flat session list
 * shape was retired — the SharePoint Sessions list covers that surface
 * with richer columns. What's left:
 *
 *   none / type / role  → matrix     (session × attendee)
 *   user                → personal   (single-user agenda)
 */
function csvShapesForFilter(filter: ExportFilter): {
  options: CsvShape[];
  default: CsvShape;
} {
  switch (filter.mode) {
    case "none":
    case "type":
    case "group":
      return { options: ["matrix"], default: "matrix" };
    case "user":
      return { options: ["personal"], default: "personal" };
  }
}

const CSV_SHAPE_LABEL: Record<CsvShape, string> = {
  matrix: "Visibility matrix — one row per (session × attendee)",
  personal: "Personal agenda — one row per session this user sees",
};

/**
 * Derive the email callable's `target` from the page filter, or return null
 * if the filter doesn't map cleanly to a recipient set (type filter — per
 * ADR decision, category scope has no coherent email audience).
 */
function filterToEmailTarget(filter: ExportFilter): string | null {
  switch (filter.mode) {
    case "none":
      return "all";
    case "group":
      return filter.groupId ? `group:${filter.groupId}` : null;
    case "user":
      return filter.user ? filter.user.id : null;
    case "type":
      return null;
  }
}

function audienceSlugForFilename(
  spec: AudienceSpec,
  persons: Person[],
  audienceById: Map<string, Audience>,
): string | undefined {
  if (spec.kind === "user") {
    const person = persons.find((p) => p.id === spec.userId);
    return person?.displayName ?? undefined;
  }
  if (spec.kind === "group") {
    return audienceById.get(spec.groupId)?.name ?? undefined;
  }
  return undefined;
}

// ── Component ────────────────────────────────────────────────────────────

export function ExportDialog({
  open,
  onClose,
  event,
  sessions,
  participants,
  persons,
  audienceById,
  userById,
  filter,
  filterSummary,
  onExportPdf,
  onSendEmail,
  sharePointUrl,
}: ExportDialogProps) {
  // Default to "sharepoint" when available — it's the highest-leverage view
  // for organizers (live-synced list, editable in place) and avoids forcing
  // a click off the default just to reach it. Falls back to PDF when
  // SharePoint isn't configured.
  const defaultAction: ExportAction = sharePointUrl ? "sharepoint" : "pdf";
  const [action, setAction] = useState<ExportAction>(defaultAction);
  const [busy, setBusy] = useState(false);
  const { venue: eventVenue } = useEventVenue(event);

  const audienceSpec = filterToAudienceSpec(filter);
  const emailTarget = filterToEmailTarget(filter);
  const emailDisabled = emailTarget === null;

  const csvShapeConfig = useMemo(() => csvShapesForFilter(filter), [filter]);
  const [csvShape, setCsvShape] = useState<CsvShape>(csvShapeConfig.default);

  // If the filter changes while the dialog is open (e.g. user tweaks the
  // page filter behind the dialog — rare, but possible), snap the shape to
  // the new default so we don't leave a stale, no-longer-valid selection.
  useEffect(() => {
    if (!csvShapeConfig.options.includes(csvShape)) {
      setCsvShape(csvShapeConfig.default);
    }
  }, [csvShapeConfig, csvShape]);

  const sessionTypes = event.config.sessionTypes;

  // Reset state on open. If the current action is "email" and email is now
  // disabled by the filter, fall back to PDF so the dialog isn't stuck in
  // an unselectable state. Also reset the CSV shape to the filter's default.
  const handleEnter = useCallback(() => {
    setAction(defaultAction);
    setCsvShape(csvShapeConfig.default);
    setBusy(false);
  }, [defaultAction, csvShapeConfig]);

  // ── Email preview ──
  const previewHtml = useMemo(() => {
    if (action !== "email" || sessions.length === 0 || emailDisabled) return "";
    const timezone = event.timezone || "America/New_York";
    const location = [eventVenue?.label, eventVenue?.city].filter(Boolean).join(", ") || null;

    const groups: { date: string; sessions: Session[] }[] = [];
    let cur = "";
    for (const s of sessions) {
      const d = s.date || "unknown";
      if (d !== cur) {
        cur = d;
        groups.push({ date: d, sessions: [] });
      }
      groups[groups.length - 1].sessions.push(s);
    }

    const getType = (id: string) => sessionTypes.find((st) => st.id === id);

    const days: AgendaEmailData["days"] = groups.map((group) => ({
      dateLabel:
        group.date !== "unknown"
          ? new Date(group.date + "T12:00:00Z").toLocaleDateString("en-US", {
              weekday: "long",
              month: "long",
              day: "numeric",
            })
          : "TBD",
      sessions: group.sessions.map((s) => {
        const st = getType(s.sessionTypeId);
        const fmt = (ts: Session["startTime"]) => {
          if (!ts || typeof ts !== "object" || !("toDate" in ts)) return "—";
          return ts.toDate().toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
            timeZone: timezone,
          });
        };
        return {
          title: s.title,
          startTime: fmt(s.startTime),
          endTime: fmt(s.endTime),
          location: s.locationLabel,
          typeName: st?.name ?? s.sessionTypeId,
          typeBackgroundColor: st?.backgroundColor ?? "#9E9E9E",
          typeTextColor: st?.textColor ?? "#FFFFFF",
        };
      }),
    }));

    return buildAgendaEmailHtml({
      recipientName:
        filter.mode === "user" && filter.user
          ? filter.user.displayName
          : null,
      eventName: event.name,
      eventStartDate: event.startDate,
      eventEndDate: event.endDate,
      eventLocation: location,
      days,
    });
  }, [action, event, sessions, sessionTypes, emailDisabled, filter]);

  // ── Handlers ──

  async function handleSubmit() {
    setBusy(true);
    try {
      if (action === "sharepoint") {
        // Pure navigation — no async work, no spinner needed in practice.
        // window.open is sync, the new-tab pop is instant.
        if (sharePointUrl) window.open(sharePointUrl, "_blank", "noopener,noreferrer");
        onClose();
        return;
      }
      if (action === "pdf") {
        await onExportPdf();
      } else if (action === "csv") {
        const rows = buildAgendaCsvRows({
          sessions,
          participants,
          persons,
          sessionTypes,
          audienceById,
          userById,
          audience: audienceSpec,
          shape: csvShape,
          timezone: event.timezone || "America/New_York",
        });
        if (rows.length <= 1) return; // only header row
        const filename = exportFilename({
          eventName: event.name,
          kind: "agenda",
          ext: "csv",
          audience: audienceSlugForFilename(audienceSpec, persons, audienceById),
        });
        downloadCsv(filename, rows);
      } else {
        // email — emailTarget guaranteed non-null by the disabled guard
        if (emailTarget === null) return;
        await onSendEmail(emailTarget);
      }
      onClose();
    } finally {
      setBusy(false);
    }
  }

  const submitLabel =
    action === "sharepoint"
      ? "Open"
      : action === "email"
        ? busy
          ? "Sending…"
          : "Send"
        : busy
          ? "Exporting…"
          : "Export";

  const submitIcon =
    action === "sharepoint" ? (
      <ExternalLink size={18} />
    ) : action === "email" ? (
      busy ? (
        <CircularProgress size={18} />
      ) : (
        <Send size={18} />
      )
    ) : busy ? (
      <CircularProgress size={18} />
    ) : (
      <Download size={18} />
    );

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      maxWidth="md"
      fullWidth
      TransitionProps={{ onEnter: handleEnter }}
      data-testid="export-dialog"
    >
      <DialogTitle>Export or Send Agenda</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          {/* ── Read-only filter summary ── */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Exporting
            </Typography>
            <Chip
              label={filterSummary}
              color="primary"
              variant="outlined"
              data-testid="export-filter-summary"
            />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 1 }}
            >
              Change the filter on the Sessions page to export a different
              scope.
            </Typography>
          </Box>

          {/* ── Action picker ── */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Format
            </Typography>
            <RadioGroup
              value={action}
              onChange={(e) => setAction(e.target.value as ExportAction)}
              data-testid="export-action-group"
            >
              <FormControlLabel
                value="pdf"
                control={<Radio data-testid="export-action-pdf" />}
                label="Download PDF — printable agenda with category color key"
              />
              <FormControlLabel
                value="csv"
                control={<Radio data-testid="export-action-csv" />}
                label={
                  audienceSpec.kind === "user"
                    ? "Download CSV — personal agenda for this user"
                    : "Download CSV — full session visibility matrix"
                }
              />
              <FormControlLabel
                value="email"
                disabled={emailDisabled}
                control={<Radio data-testid="export-action-email" />}
                label="Send via email — personalized agenda with .ics calendar attachment"
              />
              {sharePointUrl && (
                <>
                  <Divider sx={{ my: 1.5 }} />
                  <FormControlLabel
                    value="sharepoint"
                    control={<Radio data-testid="export-action-sharepoint" />}
                    label={
                      <Box>
                        <Typography variant="body2">
                          Open full sessions list in SharePoint
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Opens the event's complete sessions list in a new
                          tab. Page filters above don't apply — use SharePoint's
                          column filters to narrow the view.
                        </Typography>
                      </Box>
                    }
                  />
                </>
              )}
            </RadioGroup>
            {emailDisabled && filter.mode === "type" && (
              <Alert severity="info" sx={{ mt: 1 }}>
                Email isn't available when filtering by category — there's
                no clear recipient list. Switch the filter to a role or a
                specific user to send emails.
              </Alert>
            )}
          </Box>

          {/* ── Preview ── */}
          {action === "email" && previewHtml && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Email preview
              </Typography>
              <Box
                sx={{
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                  overflow: "hidden",
                  height: 360,
                }}
              >
                <ScaledEmailFrame srcDoc={previewHtml} title="Agenda email preview" />
              </Box>
            </Box>
          )}

          {action === "csv" && csvShapeConfig.options.length > 1 && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                CSV shape
              </Typography>
              <RadioGroup
                value={csvShape}
                onChange={(e) => setCsvShape(e.target.value as CsvShape)}
                data-testid="csv-shape-group"
              >
                {csvShapeConfig.options.map((shape) => (
                  <FormControlLabel
                    key={shape}
                    value={shape}
                    control={<Radio data-testid={`csv-shape-${shape}`} />}
                    label={CSV_SHAPE_LABEL[shape]}
                  />
                ))}
              </RadioGroup>
            </Box>
          )}

          {action === "csv" && csvShapeConfig.options.length === 1 && (
            <Typography variant="body2" color="text.secondary">
              {CSV_SHAPE_LABEL[csvShape]}.
            </Typography>
          )}

          {action === "pdf" && (
            <Typography variant="body2" color="text.secondary">
              Printable PDF of the sessions in the current filter, with a
              category color key at the top.
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          data-testid="export-submit-button"
          disabled={
            busy ||
            // SharePoint navigation is event-scoped, not filter-scoped — it's
            // immune to empty-result and empty-filter-slot guards that block
            // the actual export paths below.
            (action !== "sharepoint" &&
              (sessions.length === 0 ||
                (action === "email" && emailDisabled) ||
                // filter is selected but value slot is empty
                (filter.mode === "type" && !filter.typeId) ||
                (filter.mode === "group" && !filter.groupId) ||
                (filter.mode === "user" && !filter.user)))
          }
          startIcon={submitIcon}
        >
          {submitLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
